import { useMemo, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Divider,
	Grid2,
	MenuItem,
	Select,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SideNavigationBar from '../components/SideNavigationBar';
import { useMainCustomStyle } from '../assets/theme/Theme';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';

interface EmployeeStat {
	name: string;
	team: string;
	count: number;
}

interface CategoryStat {
	name: string;
	count: number;
	color: string;
	questions: Array<string>;
}

interface DailyStat {
	date: string;
	raised: number;
	users: number;
	categories: Record<string, number>;
}

interface KpiStat {
	label: string;
	value: string;
	change: string;
	color: string;
}

interface ProgressStat {
	label: string;
	value: number;
	color: string;
}

interface RecentConversation {
	name: string;
	channel: string;
	category: string;
	status: string;
	time: string;
}

const employees: Array<EmployeeStat> = [
	{
		name: 'Akshaya Manning',
		team: 'Integrated Marketing Communications',
		count: 17,
	},
	{ name: 'Lenin Olsen', team: 'Digital', count: 7 },
	{ name: 'Stefanie Chung', team: 'Digital', count: 7 },
	{ name: 'Hannah Fernando', team: 'Digital', count: 7 },
	{ name: 'Sameer Ompa', team: 'Digital', count: 6 },
];

const questionCategories: Array<CategoryStat> = [
	{
		name: 'Others',
		count: 33,
		color: '#EF4444',
		questions: [
			'How can I update the saved business profile?',
			'Where can I see the uploaded knowledge base status?',
			'Can the assistant answer from both PDF and website data?',
			'How do I restart a sandbox chat session?',
			'What happens after I reach my current account limit?',
		],
	},
	{
		name: 'Service',
		count: 16,
		color: '#0F8F2E',
		questions: [
			'Can a consultant take over an active customer chat?',
			'How long does the handover request stay active?',
			'Can I disable automated answers for one conversation?',
			'Where do I find pending consultant requests?',
			'Can service agents reply with attachments?',
		],
	},
	{
		name: 'Search',
		count: 14,
		color: '#184DFF',
		questions: [
			'Why does the assistant not find this product page?',
			'Can I search old conversations by phone number?',
			'How do I filter questions by date range?',
			'Can I find chats linked to a Facebook post?',
			'Does website search include sitemap pages?',
		],
	},
	{
		name: 'Created ticket',
		count: 11,
		color: '#868600',
		questions: [
			'Can a chat automatically create a support ticket?',
			'Where can I view newly created tickets?',
			'Can I change ticket priority from the portal?',
			'How do I map question categories to tickets?',
			'Will ticket status updates sync back to the customer?',
		],
	},
	{
		name: 'Web search',
		count: 4,
		color: '#F59E0B',
		questions: [
			'How often does the website content refresh?',
			'Can I exclude a webpage from assistant answers?',
			'Why is a sitemap URL not accepted?',
			'Can web search answer from product catalog pages?',
			'How do I add another website source?',
		],
	},
	{
		name: 'Ticket status',
		count: 2,
		color: '#8B02A8',
		questions: [
			'Can users check ticket status in WhatsApp?',
			'What ticket states are visible to customers?',
			'Can I hide internal ticket notes?',
			'How do status changes appear in chat?',
			'Can the assistant explain pending ticket status?',
		],
	},
];

const userCategories: Array<CategoryStat> = [
	{
		name: 'Active users',
		count: 28,
		color: '#21C17A',
		questions: [
			'Which users started conversations this week?',
			'Who has the highest question volume?',
			'Which users returned after consultant takeover?',
			'Who created a chat session today?',
			'Which users asked service questions?',
		],
	},
	{
		name: 'Engaging users',
		count: 20,
		color: '#0EA5E9',
		questions: [
			'Who asked more than three questions in one session?',
			'Which users opened multiple chat sessions?',
			'Who requested a human consultant?',
			'Which users completed a full support flow?',
			'Who returned after a previous conversation?',
		],
	},
	{
		name: 'New users',
		count: 14,
		color: '#F97316',
		questions: [
			'Who used the assistant for the first time?',
			'Which new users came from WhatsApp?',
			'Who uploaded data before testing chat?',
			'Which new users reached a usage limit?',
			'Who created an account from the sandbox page?',
		],
	},
	{
		name: 'Consultant users',
		count: 9,
		color: '#A855F7',
		questions: [
			'Who needed consultant support today?',
			'Which users waited for takeover?',
			'Who continued chatting after consultant reply?',
			'Which consultant sessions were resolved?',
			'Who sent files during consultant mode?',
		],
	},
];

const dailyStats: Array<DailyStat> = [
	{
		date: '2026-08-01',
		raised: 0,
		users: 0,
		categories: {},
	},
	{
		date: '2026-08-02',
		raised: 0,
		users: 0,
		categories: {},
	},
	{
		date: '2026-08-03',
		raised: 15,
		users: 5,
		categories: {
			'Created ticket': 3,
			Others: 4,
			Search: 3,
			Service: 4,
			'Ticket status': 0,
			'Web search': 1,
		},
	},
	{
		date: '2026-08-04',
		raised: 24,
		users: 6,
		categories: {
			'Created ticket': 2,
			Others: 6,
			Search: 3,
			Service: 4,
			'Ticket status': 1,
			'Web search': 2,
		},
	},
	{
		date: '2026-08-05',
		raised: 54,
		users: 8,
		categories: {
			'Created ticket': 6,
			Others: 22,
			Search: 7,
			Service: 9,
			'Ticket status': 0,
			'Web search': 1,
		},
	},
	{
		date: '2026-08-06',
		raised: 2,
		users: 2,
		categories: {
			'Created ticket': 0,
			Others: 1,
			Search: 1,
			Service: 0,
			'Ticket status': 0,
			'Web search': 0,
		},
	},
];

const userDailyStats = dailyStats.map(stat => ({
	...stat,
	raised: Math.max(0, Math.round(stat.raised * 0.6)),
	users: Math.max(0, stat.users + (stat.raised > 0 ? 3 : 0)),
	categories: {
		'Active users': Math.max(0, Math.round(stat.raised * 0.25)),
		'Engaging users': Math.max(0, Math.round(stat.raised * 0.2)),
		'New users': Math.max(0, Math.round(stat.raised * 0.1)),
		'Consultant users': Math.max(0, Math.round(stat.raised * 0.05)),
	},
}));

const kpiStats: Array<KpiStat> = [
	{
		label: 'Active users',
		value: '20',
		change: '+6 today',
		color: MAIN_THEME_COLOR,
	},
	{
		label: 'Consultant requests',
		value: '12',
		change: '3 pending',
		color: '#0EA5E9',
	},
	{
		label: 'Unanswered',
		value: '8',
		change: 'Needs review',
		color: '#F97316',
	},
	{
		label: 'Resolved tickets',
		value: '27',
		change: '84% resolved',
		color: '#6366F1',
	},
];

const channelSplit: Array<ProgressStat> = [
	{ label: 'WhatsApp', value: 62, color: MAIN_THEME_COLOR },
	{ label: 'Website', value: 25, color: '#0EA5E9' },
	{ label: 'Facebook', value: 13, color: '#6366F1' },
];

const takeoverStats: Array<ProgressStat> = [
	{ label: 'Accepted', value: 58, color: MAIN_THEME_COLOR },
	{ label: 'Pending', value: 25, color: '#F59E0B' },
	{ label: 'Missed', value: 17, color: '#EF4444' },
];

const unansweredQuestions = [
	'Can I connect two WhatsApp numbers to one assistant?',
	'How do I change the default consultant assignment?',
	'Does the assistant support Sinhala and Tamil together?',
	'Can ticket priority be detected from the customer message?',
];

const recentConversations: Array<RecentConversation> = [
	{
		name: 'Nimal Perera',
		channel: 'WhatsApp',
		category: 'Service',
		status: 'Consultant',
		time: '8 min ago',
	},
	{
		name: 'Amaya Silva',
		channel: 'Website',
		category: 'Search',
		status: 'Active',
		time: '14 min ago',
	},
	{
		name: 'Kavin Raj',
		channel: 'Facebook',
		category: 'Ticket status',
		status: 'Resolved',
		time: '23 min ago',
	},
	{
		name: 'Sara John',
		channel: 'WhatsApp',
		category: 'Others',
		status: 'Pending',
		time: '31 min ago',
	},
];

const chartWidth = 760;
const chartHeight = 280;
const chartPadding = { top: 26, right: 30, bottom: 82, left: 70 };

function getPoint(index: number, value: number, maxValue: number) {
	const plotWidth = chartWidth - chartPadding.left - chartPadding.right;
	const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;
	const x =
		chartPadding.left + (plotWidth / (dailyStats.length - 1 || 1)) * index;
	const y =
		chartPadding.top +
		plotHeight -
		(value / Math.max(maxValue, 1)) * plotHeight;

	return { x, y };
}

function LineChart({ data }: { data: Array<DailyStat> }) {
	const maxValue = Math.max(...data.map(stat => stat.raised), 60);
	const raisedPath = data
		.map((stat, index) => {
			const point = getPoint(index, stat.raised, maxValue);
			return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
		})
		.join(' ');
	const usersPath = data
		.map((stat, index) => {
			const point = getPoint(index, stat.users, maxValue);
			return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
		})
		.join(' ');
	const ticks = [0, 15, 30, 45, 60];

	return (
		<Box sx={chartScrollStyle}>
			<svg
				viewBox={`0 0 ${chartWidth} ${chartHeight}`}
				width="100%"
				role="img"
				aria-label="Questions raised and engaging users line chart">
				{ticks.map(tick => {
					const point = getPoint(0, tick, maxValue);
					return (
						<g key={tick}>
							<line
								x1={chartPadding.left}
								x2={chartWidth - chartPadding.right}
								y1={point.y}
								y2={point.y}
								stroke="#EEF2F7"
								strokeWidth="1"
							/>
							<text
								x={chartPadding.left - 16}
								y={point.y + 4}
								textAnchor="end"
								fontSize="11"
								fill="#9CA3AF">
								{tick}
							</text>
						</g>
					);
				})}
				<path
					d={raisedPath}
					fill="none"
					stroke="#4EA6DF"
					strokeWidth="2.2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d={usersPath}
					fill="none"
					stroke="#E99683"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				{data.map((stat, index) => {
					const x = getPoint(index, stat.raised, maxValue).x;
					return (
						<g key={stat.date}>
							<text
								x={x}
								y={chartHeight - 44}
								textAnchor="middle"
								fontSize="11"
								fill="#9CA3AF">
								{stat.date}
							</text>
						</g>
					);
				})}
				<text
					x={chartWidth / 2}
					y={chartHeight - 10}
					textAnchor="middle"
					fontSize="11"
					fill="#6B7280">
					Daily
				</text>
				<text
					x="18"
					y={chartHeight / 2}
					textAnchor="middle"
					fontSize="11"
					fill="#6B7280"
					transform={`rotate(-90 18 ${chartHeight / 2})`}>
					Questions raised & users
				</text>
			</svg>
		</Box>
	);
}

function StackedBarChart({
	data,
	categories,
}: {
	data: Array<DailyStat>;
	categories: Array<CategoryStat>;
}) {
	const maxValue = Math.max(
		...data.map(stat =>
			categories.reduce(
				(total, category) =>
					total + (stat.categories[category.name] || 0),
				0,
			),
		),
		50,
	);
	const plotWidth = chartWidth - chartPadding.left - chartPadding.right;
	const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;
	const barWidth = 52;

	function getBarHeight(value: number) {
		return (value / Math.max(maxValue, 1)) * plotHeight;
	}

	return (
		<Box sx={chartScrollStyle}>
			<svg
				viewBox={`0 0 ${chartWidth} ${chartHeight}`}
				width="100%"
				role="img"
				aria-label="Questions by category stacked bar chart">
				{[0, 10, 20, 30, 40, 50].map(tick => {
					const y =
						chartPadding.top +
						plotHeight -
						(tick / Math.max(maxValue, 1)) * plotHeight;
					return (
						<g key={tick}>
							<line
								x1={chartPadding.left}
								x2={chartWidth - chartPadding.right}
								y1={y}
								y2={y}
								stroke="#F1F5F9"
								strokeWidth="1"
							/>
							<text
								x={chartPadding.left - 16}
								y={y + 4}
								textAnchor="end"
								fontSize="11"
								fill="#9CA3AF">
								{tick}
							</text>
						</g>
					);
				})}
				{data.map((stat, index) => {
					const centerX =
						chartPadding.left +
						(plotWidth / (data.length - 1 || 1)) * index;
					let bottomY = chartPadding.top + plotHeight;

					return (
						<g key={stat.date}>
							{categories.map(category => {
								const value =
									stat.categories[category.name] || 0;
								const height = getBarHeight(value);
								const y = bottomY - height;
								bottomY = y;

								return (
									<rect
										key={category.name}
										x={centerX - barWidth / 2}
										y={y}
										width={barWidth}
										height={height}
										fill={category.color}
										rx="1"
									/>
								);
							})}
							<text
								x={centerX}
								y={chartHeight - 48}
								textAnchor="middle"
								fontSize="11"
								fill="#9CA3AF"
								transform={`rotate(-90 ${centerX} ${
									chartHeight - 48
								})`}>
								{stat.date}
							</text>
						</g>
					);
				})}
				<text
					x={chartWidth / 2}
					y={chartHeight - 10}
					textAnchor="middle"
					fontSize="11"
					fill="#6B7280">
					Daily
				</text>
				<text
					x="18"
					y={chartHeight / 2}
					textAnchor="middle"
					fontSize="11"
					fill="#6B7280"
					transform={`rotate(-90 18 ${chartHeight / 2})`}>
					Questions raised
				</text>
			</svg>
		</Box>
	);
}

function Panel({
	children,
	sx,
}: {
	children: React.ReactNode;
	sx?: Record<string, unknown>;
}) {
	return (
		<Box
			sx={{
				backgroundColor: '#FFFFFF',
				border: '1px solid #E7EDF4',
				borderRadius: '6px',
				boxShadow: '0px 2px 8px rgba(15, 23, 42, 0.03)',
				...sx,
			}}>
			{children}
		</Box>
	);
}

function Legend({
	categories,
	includeLines = false,
}: {
	categories: Array<CategoryStat>;
	includeLines?: boolean;
}) {
	return (
		<Box
			sx={{
				display: 'flex',
				gap: '14px',
				flexWrap: 'wrap',
				alignItems: 'center',
				mb: 1,
			}}>
			{includeLines && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Box
							sx={{
								width: 11,
								height: 2,
								backgroundColor: '#4EA6DF',
							}}
						/>
						<Typography fontSize="12px" color="#64748B">
							Questions Raised
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Box
							sx={{
								width: 11,
								height: 2,
								backgroundColor: '#E99683',
							}}
						/>
						<Typography fontSize="12px" color="#64748B">
							Engaging Users
						</Typography>
					</Box>
				</>
			)}
			{categories.map(category => (
				<Box
					key={category.name}
					sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: '2px',
							backgroundColor: category.color,
						}}
					/>
					<Typography fontSize="12px" color="#64748B">
						{category.name}
					</Typography>
				</Box>
			))}
		</Box>
	);
}

function KpiTile({ item }: { item: KpiStat }) {
	return (
		<Panel sx={{ p: 2, height: '100%' }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					gap: 1,
				}}>
				<Box>
					<Typography
						fontSize="12px"
						color="#64748B"
						fontWeight="600">
						{item.label}
					</Typography>
					<Typography
						fontSize="25px"
						color="#111827"
						fontWeight="700"
						lineHeight={1.35}>
						{item.value}
					</Typography>
				</Box>
				<Box
					sx={{
						width: 10,
						height: 10,
						borderRadius: '50%',
						backgroundColor: item.color,
						mt: 0.5,
					}}
				/>
			</Box>
			<Typography fontSize="12px" color={item.color} fontWeight="600">
				{item.change}
			</Typography>
		</Panel>
	);
}

function ProgressRow({ item }: { item: ProgressStat }) {
	return (
		<Box sx={{ py: 0.8 }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					mb: 0.8,
				}}>
				<Typography fontSize="13px" color="#334155" fontWeight="600">
					{item.label}
				</Typography>
				<Typography fontSize="13px" color="#334155" fontWeight="700">
					{item.value}%
				</Typography>
			</Box>
			<Box
				sx={{
					height: 6,
					borderRadius: '20px',
					backgroundColor: '#F1F5F9',
					overflow: 'hidden',
				}}>
				<Box
					sx={{
						width: `${item.value}%`,
						height: '100%',
						backgroundColor: item.color,
						borderRadius: '20px',
					}}
				/>
			</Box>
		</Box>
	);
}

function StatusChip({ label }: { label: string }) {
	const colorMap: Record<string, string> = {
		Active: MAIN_THEME_COLOR,
		Consultant: '#0EA5E9',
		Resolved: '#6366F1',
		Pending: '#F59E0B',
	};

	return (
		<Chip
			label={label}
			size="small"
			sx={{
				height: 24,
				borderRadius: '5px',
				backgroundColor: `${colorMap[label] || '#64748B'}18`,
				color: colorMap[label] || '#64748B',
				fontSize: '11px',
				fontWeight: 700,
			}}
		/>
	);
}

function Dashboard() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const mainDivStyle = useMainCustomStyle();
	const [groupBy, setGroupBy] = useState('All');
	const [rangeType, setRangeType] = useState('Day');
	const [categoryMode, setCategoryMode] = useState<'question' | 'user'>(
		'question',
	);
	const [selectedCategory, setSelectedCategory] =
		useState<CategoryStat | null>(null);
	const activeCategories =
		categoryMode === 'question' ? questionCategories : userCategories;
	const activeDailyStats =
		categoryMode === 'question' ? dailyStats : userDailyStats;

	const categoryTotal = useMemo(
		() =>
			activeCategories.reduce(
				(total, category) => total + category.count,
				0,
			),
		[activeCategories],
	);
	const totalQuestions = categoryTotal;
	const totalEmployees = 20;
	const questionsPerEmployee = Math.round(totalQuestions / totalEmployees);

	function getPercentage(count: number) {
		return `${((count / Math.max(totalQuestions, 1)) * 100).toFixed(1)}%`;
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid2
					container
					sx={{
						px: { xs: '14px', md: '24px' },
						pb: '26px',
						width: '100%',
					}}>
					<Grid2
						container
						sx={{
							width: '100%',
							alignItems: { xs: 'flex-start', md: 'center' },
							justifyContent: 'space-between',
							mb: 1.5,
							gap: 1,
						}}>
						<Box>
							<Typography
								fontSize="22px"
								fontWeight="700"
								color="#0F172A">
								Dashboard
							</Typography>
							<Typography fontSize="13px" color="#64748B">
								Monitor questions, users, categories, and
								sandbox activity.
							</Typography>
						</Box>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								flexWrap: 'wrap',
							}}>
							<Typography
								fontSize="12px"
								color="#334155"
								fontWeight="600">
								Statistics from:
							</Typography>
							<Select
								size="small"
								value={groupBy}
								onChange={event =>
									setGroupBy(event.target.value)
								}
								sx={filterStyle}>
								<MenuItem value="All">All</MenuItem>
								<MenuItem value="WhatsApp">WhatsApp</MenuItem>
								<MenuItem value="Website">Website</MenuItem>
							</Select>
							<Select
								size="small"
								value={rangeType}
								onChange={event =>
									setRangeType(event.target.value)
								}
								sx={filterStyle}>
								<MenuItem value="Day">Day</MenuItem>
								<MenuItem value="Week">Week</MenuItem>
								<MenuItem value="Month">Month</MenuItem>
							</Select>
							<Button
								variant="outlined"
								sx={{
									...dateButtonStyle,
									minWidth: isMobile ? '100%' : '210px',
								}}>
								2026-08-01 → 2026-08-06
							</Button>
						</Box>
					</Grid2>

					<Grid2 container spacing={1.25} width="100%" mb={1.25}>
						{kpiStats.map(item => (
							<Grid2
								key={item.label}
								size={{ xs: 12, sm: 6, lg: 3 }}>
								<KpiTile item={item} />
							</Grid2>
						))}
					</Grid2>

					<Grid2 container spacing={1.25} width="100%">
						<Grid2
							container
							size={{ xs: 12, lg: 4 }}
							sx={{
								flexDirection: 'column',
								gap: 1.25,
							}}>
							<Panel sx={{ p: 2.5 }}>
								<Typography
									fontSize="18px"
									fontWeight="700"
									color="#1F2937"
									mb={1.8}>
									Summary
								</Typography>
								{[
									{
										label: 'Total questions raised',
										value: totalQuestions,
									},
									{
										label: 'Employees',
										value: totalEmployees,
									},
									{
										label: 'Questions per employee',
										value: questionsPerEmployee,
									},
								].map(item => (
									<Box
										key={item.label}
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											py: 0.9,
										}}>
										<Typography
											fontSize="14px"
											color="#4B5563"
											fontWeight="500">
											{item.label}
										</Typography>
										<Typography
											fontSize="21px"
											color="#1F2937"
											fontWeight="600">
											{item.value}
										</Typography>
									</Box>
								))}
							</Panel>

							<Panel sx={{ p: 2.5 }}>
								<Typography
									fontSize="18px"
									fontWeight="700"
									color="#1F2937"
									mb={2}>
									Top 5 employees
								</Typography>
								{employees.map(employee => (
									<Box
										key={employee.name}
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1.5,
											py: 0.75,
										}}>
										<Box
											sx={{
												width: 34,
												height: 34,
												borderRadius: '50%',
												backgroundColor: '#F1F5F9',
												position: 'relative',
												flexShrink: 0,
												'&:before': {
													content: '""',
													position: 'absolute',
													width: 12,
													height: 12,
													borderRadius: '50%',
													border: '2px solid #D1D5DB',
													left: 10,
													top: 7,
												},
												'&:after': {
													content: '""',
													position: 'absolute',
													width: 22,
													height: 11,
													borderRadius:
														'12px 12px 4px 4px',
													border: '2px solid #D1D5DB',
													borderBottom: 0,
													left: 6,
													top: 21,
												},
											}}
										/>
										<Box sx={{ minWidth: 0, flex: 1 }}>
											<Typography
												fontSize="13px"
												fontWeight="700"
												color="#111827"
												noWrap>
												{employee.name}
											</Typography>
											<Typography
												fontSize="12px"
												fontStyle="italic"
												color="#6B7280"
												noWrap>
												{employee.team}
											</Typography>
										</Box>
										<Typography
											fontSize="20px"
											fontWeight="500"
											color="#1F2937">
											{employee.count}
										</Typography>
									</Box>
								))}
							</Panel>

							<Panel sx={{ p: 2.5 }}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										gap: 1,
										alignItems: 'center',
										mb: 2,
									}}>
									<Box>
										<Typography
											fontSize="18px"
											fontWeight="700"
											color="#1F2937">
											Questions in categories
										</Typography>
										<Typography
											fontSize="12px"
											color="#64748B">
											Click a row to show last 5 questions
										</Typography>
									</Box>
									<Box
										sx={{
											display: 'flex',
											gap: 1,
											flexWrap: 'wrap',
											justifyContent: 'flex-end',
										}}>
										<Select
											size="small"
											value={categoryMode}
											onChange={event => {
												setCategoryMode(
													event.target.value as
														| 'question'
														| 'user',
												);
												setSelectedCategory(null);
											}}
											sx={{
												...filterStyle,
												minWidth: 150,
											}}>
											<MenuItem value="question">
												Question related
											</MenuItem>
											<MenuItem value="user">
												User related
											</MenuItem>
										</Select>
									</Box>
								</Box>
								{activeCategories.map(category => (
									<Box
										key={category.name}
										onClick={() =>
											setSelectedCategory(category)
										}
										sx={{
											display: 'grid',
											gridTemplateColumns:
												'1fr auto auto',
											alignItems: 'center',
											columnGap: 1.5,
											py: 1.25,
											cursor: 'pointer',
											borderBottom: '1px solid #EEF2F7',
											'&:last-of-type': {
												borderBottom: 'none',
											},
											'&:hover': {
												backgroundColor: '#F8FAFC',
											},
										}}>
										<Typography
											fontSize="13px"
											fontWeight="600"
											color="#374151">
											{category.name}
										</Typography>
										<Typography
											fontSize="16px"
											fontWeight="700"
											color="#1F2937">
											{category.count}
										</Typography>
										<Chip
											label={getPercentage(
												category.count,
											)}
											size="small"
											sx={{
												height: 28,
												minWidth: 62,
												borderRadius: '5px',
												backgroundColor: category.color,
												color: '#FFFFFF',
												fontWeight: 600,
											}}
										/>
									</Box>
								))}
							</Panel>

							<Panel sx={{ p: 2.5 }}>
								<Typography
									fontSize="18px"
									fontWeight="700"
									color="#1F2937">
									Unanswered questions
								</Typography>
								<Typography
									fontSize="12px"
									color="#64748B"
									mb={1.4}>
									Knowledge gaps to review
								</Typography>
								{unansweredQuestions.map((question, index) => (
									<Box
										key={question}
										sx={{
											display: 'flex',
											gap: 1.2,
											py: 0.95,
											borderBottom: '1px solid #EEF2F7',
											'&:last-of-type': {
												borderBottom: 'none',
											},
										}}>
										<Box
											sx={{
												width: 22,
												height: 22,
												borderRadius: '50%',
												backgroundColor: '#FFF7ED',
												color: '#F97316',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '11px',
												fontWeight: 800,
												flexShrink: 0,
											}}>
											{index + 1}
										</Box>
										<Typography
											fontSize="13px"
											color="#334155"
											lineHeight={1.45}>
											{question}
										</Typography>
									</Box>
								))}
							</Panel>
						</Grid2>

						<Grid2
							container
							size={{ xs: 12, lg: 8 }}
							sx={{
								flexDirection: 'column',
								gap: 1.25,
							}}>
							<Panel sx={{ p: { xs: 2, md: 2.5 } }}>
								<Legend categories={[]} includeLines />
								<LineChart data={activeDailyStats} />
							</Panel>
							<Panel sx={{ p: { xs: 2, md: 2.5 } }}>
								<Legend categories={activeCategories} />
								<StackedBarChart
									data={activeDailyStats}
									categories={activeCategories}
								/>
							</Panel>
							<Grid2 container spacing={1.25} width="100%">
								<Grid2 size={{ xs: 12, md: 4 }}>
									<Panel sx={{ p: 2.5, height: '100%' }}>
										<Typography
											fontSize="16px"
											fontWeight="700"
											color="#111827">
											Trend insight
										</Typography>
										<Typography
											fontSize="13px"
											color="#64748B"
											lineHeight={1.55}
											mt={1}>
											Questions peaked on 2026-08-05.
											Search and service topics are
											driving most of the traffic this
											period.
										</Typography>
										<Chip
											label="+42% vs previous period"
											size="small"
											sx={{
												mt: 1.8,
												borderRadius: '5px',
												backgroundColor: '#ECFDF5',
												color: MAIN_THEME_COLOR,
												fontWeight: 700,
											}}
										/>
									</Panel>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									<Panel sx={{ p: 2.5, height: '100%' }}>
										<Typography
											fontSize="16px"
											fontWeight="700"
											color="#111827">
											Channel split
										</Typography>
										<Typography
											fontSize="12px"
											color="#64748B">
											Where questions came from
										</Typography>
										<Box mt={1.2}>
											{channelSplit.map(item => (
												<ProgressRow
													key={item.label}
													item={item}
												/>
											))}
										</Box>
									</Panel>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									<Panel sx={{ p: 2.5, height: '100%' }}>
										<Typography
											fontSize="16px"
											fontWeight="700"
											color="#111827">
											Consultant takeover
										</Typography>
										<Typography
											fontSize="12px"
											color="#64748B">
											12 total handover requests
										</Typography>
										<Box mt={1.2}>
											{takeoverStats.map(item => (
												<ProgressRow
													key={item.label}
													item={item}
												/>
											))}
										</Box>
									</Panel>
								</Grid2>
							</Grid2>
							<Panel sx={{ p: 2.5 }}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										gap: 1,
										mb: 1.5,
									}}>
									<Box>
										<Typography
											fontSize="16px"
											fontWeight="700"
											color="#111827">
											Recent conversations
										</Typography>
										<Typography
											fontSize="12px"
											color="#64748B">
											Latest active customer sessions
										</Typography>
									</Box>
									<Button
										size="small"
										sx={{
											textTransform: 'none',
											color: MAIN_THEME_COLOR,
											fontWeight: 700,
										}}>
										View all
									</Button>
								</Box>
								{recentConversations.map(conversation => (
									<Box
										key={`${conversation.name}-${conversation.time}`}
										sx={{
											display: 'grid',
											gridTemplateColumns: {
												xs: '1fr',
												md: '1.5fr 1fr 1fr auto auto',
											},
											gap: { xs: 0.6, md: 1.2 },
											alignItems: 'center',
											py: 1.1,
											borderBottom: '1px solid #EEF2F7',
											'&:last-of-type': {
												borderBottom: 'none',
											},
										}}>
										<Typography
											fontSize="13px"
											color="#111827"
											fontWeight="700">
											{conversation.name}
										</Typography>
										<Typography
											fontSize="12px"
											color="#64748B">
											{conversation.channel}
										</Typography>
										<Typography
											fontSize="12px"
											color="#64748B">
											{conversation.category}
										</Typography>
										<StatusChip
											label={conversation.status}
										/>
										<Typography
											fontSize="12px"
											color="#94A3B8"
											textAlign={{
												xs: 'left',
												md: 'right',
											}}>
											{conversation.time}
										</Typography>
									</Box>
								))}
							</Panel>
							{selectedCategory && (
								<Panel sx={{ p: 2.5 }}>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											gap: 1,
											mb: 1,
										}}>
										<Box>
											<Typography
												fontSize="16px"
												fontWeight="700"
												color="#111827">
												{selectedCategory.name}
											</Typography>
											<Typography
												fontSize="12px"
												color="#64748B">
												Last 5 questions · {groupBy} ·{' '}
												{rangeType}
											</Typography>
										</Box>
										<Button
											size="small"
											onClick={() =>
												setSelectedCategory(null)
											}
											sx={{
												textTransform: 'none',
												color: '#64748B',
											}}>
											Close
										</Button>
									</Box>
									<Divider sx={{ mb: 1.5 }} />
									{selectedCategory.questions.map(
										(question, index) => (
											<Box
												key={question}
												sx={{
													display: 'flex',
													gap: 1.5,
													py: 1,
													alignItems: 'flex-start',
												}}>
												<Box
													sx={{
														width: 24,
														height: 24,
														borderRadius: '50%',
														backgroundColor:
															'#ECFDF5',
														color: MAIN_THEME_COLOR,
														fontSize: '12px',
														fontWeight: 700,
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'center',
														flexShrink: 0,
													}}>
													{index + 1}
												</Box>
												<Typography
													fontSize="13px"
													color="#334155"
													lineHeight={1.6}>
													{question}
												</Typography>
											</Box>
										),
									)}
								</Panel>
							)}
						</Grid2>
					</Grid2>
				</Grid2>
			</div>
		</>
	);
}

const filterStyle = {
	height: 36,
	minWidth: 96,
	fontSize: '13px',
	backgroundColor: '#FFFFFF',
	borderRadius: '6px',
	'& .MuiOutlinedInput-notchedOutline': {
		borderColor: '#E2E8F0',
	},
};

const chartScrollStyle = {
	overflowX: 'auto',
	width: '100%',
	height: { xs: 270, md: 280 },
	'& svg': {
		display: 'block',
		height: '100%',
		minWidth: { xs: 640, md: '100%' },
	},
};

const dateButtonStyle = {
	height: 36,
	borderRadius: '6px',
	borderColor: '#E2E8F0',
	color: '#475569',
	backgroundColor: '#FFFFFF',
	textTransform: 'none',
	fontSize: '13px',
	justifyContent: 'space-between',
};

export default Dashboard;
